/**
 * Service worker registration for offline play.
 *
 * Registration is deliberately refused in dev and inside the Lovable preview
 * iframe, and `?sw=off` acts as a kill switch that unregisters the worker.
 */
// Keep a release query on the worker URL so the publisher and browsers cannot
// mistake a newly built worker for the previously deployed offline bundle.
const SW_PATH = "/sw.js";
const SW_URL = `${SW_PATH}?release=2026-09-16-1`;
const RELOAD_GUARD_KEY = "savey-sw-update-reload";

const unregisterAppSW = async () => {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.allSettled(
    regs
      .filter((r) => {
        const scriptURL = r.active?.scriptURL || r.installing?.scriptURL;
        if (!scriptURL) return false;

        try {
          return new URL(scriptURL).pathname === SW_PATH;
        } catch {
          return scriptURL.endsWith(SW_PATH);
        }
      })
      .map((r) => r.unregister()),
  );
};

const isRefusedContext = () => {
  if (!import.meta.env.PROD) return true;
  if (window.self !== window.top) return true;
  if (new URL(window.location.href).searchParams.get("sw") === "off") return true;

  const h = window.location.hostname;
  return (
    h.startsWith("id-preview--") ||
    h.startsWith("preview--") ||
    h === "lovableproject.com" ||
    h.endsWith(".lovableproject.com") ||
    h === "lovableproject-dev.com" ||
    h.endsWith(".lovableproject-dev.com") ||
    h === "beta.lovable.dev" ||
    h.endsWith(".beta.lovable.dev")
  );
};

export const registerOfflineSW = () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  if (isRefusedContext()) {
    void unregisterAppSW();
    return;
  }

  // A newly installed worker takes control immediately (skipWaiting + clientsClaim).
  // Reload an existing app once so it runs the matching, newly cached JS bundle.
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloading) return;

    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0");
    if (Date.now() - lastReload < 30_000) return;

    reloading = true;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // Registration checks may otherwise be throttled by the browser.
        if (navigator.onLine) void registration.update();

        const checkForUpdate = () => {
          if (document.visibilityState === "visible" && navigator.onLine) {
            void registration.update();
          }
        };

        window.addEventListener("focus", checkForUpdate);
        document.addEventListener("visibilitychange", checkForUpdate);
      })
      .catch(() => {
        /* offline support is best-effort */
      });
  });
};
